import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Lock, Server, Globe2, Eye, FileText, Activity,
  CheckCircle2, ExternalLink, Building2,
} from "lucide-react";

const SECURITY_PILLARS = [
  {
    Icon: Lock,
    title: "Encryption",
    bullets: [
      "TLS 1.3 in transit (HSTS preloaded)",
      "AES-256 at rest on all primary stores",
      "Per-publisher API key hashing (SHA-256)",
      "Webhook payload HMAC signing available",
    ],
  },
  {
    Icon: ShieldCheck,
    title: "Access control",
    bullets: [
      "Role-based access (4 roles, 12+ permissions)",
      "Two-factor authentication available on every account",
      "Required on all admin accounts",
      "All admin actions audited and timestamped",
    ],
  },
  {
    Icon: Server,
    title: "Infrastructure",
    bullets: [
      "EU-West (Ireland) primary region",
      "Daily automated backups, 30-day retention",
      "Cloudflare WAF + DDoS protection",
      "No public database endpoints",
    ],
  },
  {
    Icon: Eye,
    title: "Monitoring",
    bullets: [
      "24/7 platform health monitoring",
      "Anomaly alerts for unusual ingestion patterns",
      "Quarterly penetration testing",
      "Continuous vulnerability scanning",
    ],
  },
];

const SUBPROCESSORS = [
  { name: "MongoDB Atlas",       purpose: "Primary database",            region: "EU-West (Ireland)", url: "https://www.mongodb.com/legal/trust" },
  { name: "Stripe Payments UK",  purpose: "Card payments + Connect payouts", region: "UK / EU",      url: "https://stripe.com/privacy" },
  { name: "Anthropic",           purpose: "AI inference (Claude)",       region: "US (zero-retention)", url: "https://www.anthropic.com/legal/privacy" },
  { name: "OpenAI",              purpose: "AI inference",                region: "US (zero-retention)", url: "https://openai.com/policies/privacy-policy" },
  { name: "Google Cloud (Gemini)", purpose: "AI inference",              region: "EU / US",          url: "https://cloud.google.com/terms/data-processing-addendum" },
  { name: "Cloudflare",          purpose: "CDN / WAF / DDoS",            region: "Global edge",       url: "https://www.cloudflare.com/privacypolicy/" },
];

const COMPLIANCE = [
  { status: "live", label: "UK GDPR aligned" },
  { status: "live", label: "ICO registration ZC148599" },
  { status: "live", label: "FCA-aware lead workflows (CONC, MCOB, ICOBS)" },
  { status: "live", label: "TrustedForm / consent token capture" },
  { status: "live", label: "Encryption in transit (TLS 1.3) and at rest (AES-256)" },
  { status: "live", label: "Daily encrypted backups, 30-day retention" },
  { status: "live", label: "Quarterly penetration testing" },
];

export default function TrustCenter() {
  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50/50 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">
            Trust &amp; Compliance
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-[-0.04em] font-semibold text-slate-900 leading-[1.02] max-w-3xl" data-testid="trust-title">
            Security, privacy and compliance — built into the platform.
          </h1>
          <p className="mt-5 max-w-2xl text-[17px] text-slate-600 leading-relaxed">
            Connexis Group is the routing infrastructure behind regulated UK financial brands. This page is the
            single source of truth for how we protect your data, your customers' enquiries, and your reputation.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="mailto:legal@connex.is"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white px-5 h-11 text-sm font-medium shadow-[0_8px_24px_rgba(0,85,255,0.35)]"
              data-testid="trust-security-contact"
            >
              legal@connex.is
            </a>
            <Link
              to="/status"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 hover:border-[#0055FF]/40 px-5 h-11 text-sm font-medium text-slate-700"
              data-testid="trust-status-link"
            >
              <Activity className="size-4" /> Live status
            </Link>
          </div>
        </div>
      </section>

      {/* CORPORATE / REGULATORY */}
      <section className="py-16 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">
              <Building2 className="size-3.5" strokeWidth={2.2} /> Corporate
            </div>
            <h2 className="mt-3 text-3xl tracking-[-0.025em] font-semibold text-slate-900">Who we are</h2>
            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6 text-[14.5px] text-slate-700 leading-[1.75] shadow-soft">
              <div><strong>Legal entity</strong>: Connexis Group Ltd</div>
              <div><strong>Company number</strong>: <span className="font-mono">17214446</span></div>
              <div><strong>Registered office</strong>: 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom</div>
              <div><strong>Jurisdiction</strong>: England &amp; Wales</div>
              <div><strong>ICO registration</strong>: <span className="font-mono">ZC148599</span></div>
              <div className="mt-3"><strong>Data Protection contact</strong>: <a href="mailto:legal@connex.is" className="text-[#0055FF] hover:underline">legal@connex.is</a></div>
            </div>
          </div>

          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">
              <CheckCircle2 className="size-3.5" strokeWidth={2.2} /> Compliance posture
            </div>
            <h2 className="mt-3 text-3xl tracking-[-0.025em] font-semibold text-slate-900">Standards we align to</h2>
            <ul className="mt-5 space-y-2">
              {COMPLIANCE.map(({ label }) => (
                <li key={label} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-[14px]">
                  <span className="text-slate-800">{label}</span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-mono font-semibold tracking-[0.16em] uppercase text-emerald-600">
                    <CheckCircle2 className="size-3" /> Live
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SECURITY PILLARS */}
      <section className="py-16 border-b border-slate-200 bg-slate-50/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">Security</div>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-[-0.025em] font-semibold text-slate-900 max-w-2xl">
            Four pillars holding up the platform.
          </h2>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SECURITY_PILLARS.map(({ Icon, title, bullets }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#0055FF]/30 hover:shadow-md transition-all">
                <span className="inline-grid place-items-center size-11 rounded-xl bg-[#0055FF]/[0.08] text-[#0055FF] ring-1 ring-[#0055FF]/15">
                  <Icon className="size-5" strokeWidth={1.7} />
                </span>
                <h3 className="mt-4 text-[18px] font-semibold tracking-tight text-slate-900">{title}</h3>
                <ul className="mt-3 space-y-1.5">
                  {bullets.map((b) => (
                    <li key={b} className="flex items-start gap-1.5 text-[13px] text-slate-600 leading-relaxed">
                      <CheckCircle2 className="size-3 text-emerald-500 mt-1 flex-shrink-0" strokeWidth={2.4} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUBPROCESSORS */}
      <section className="py-16 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">Sub-processors</div>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-[-0.025em] font-semibold text-slate-900 max-w-2xl">
            Every third party we use, and what they do.
          </h2>
          <p className="mt-3 max-w-2xl text-[14.5px] text-slate-600">
            We notify all customers <strong>14 days before</strong> adding or changing any sub-processor.
          </p>

          <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-5 py-3">Sub-processor</th>
                  <th className="text-left px-5 py-3">Purpose</th>
                  <th className="text-left px-5 py-3">Region</th>
                  <th className="text-left px-5 py-3">Policy</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {SUBPROCESSORS.map((s) => (
                  <tr key={s.name} className="hover:bg-slate-50/40">
                    <td className="px-5 py-3.5 font-medium text-slate-900">{s.name}</td>
                    <td className="px-5 py-3.5 text-slate-600">{s.purpose}</td>
                    <td className="px-5 py-3.5 text-slate-600 font-mono text-[12.5px]">{s.region}</td>
                    <td className="px-5 py-3.5">
                      <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0055FF] hover:underline text-[13px]">
                        View <ExternalLink className="size-3" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* POLICY DOCS */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">Policy documents</div>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-[-0.025em] font-semibold text-slate-900 max-w-2xl">
            Read the legal pages.
          </h2>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              ["/legal/privacy",          "Privacy Policy"],
              ["/legal/cookies",          "Cookie Policy"],
              ["/legal/terms-buyer",      "Buyer Terms"],
              ["/legal/terms-publisher",  "Publisher Terms"],
              ["/legal/terms-affiliate",  "Affiliate Terms"],
              ["/legal/dpa",              "Data Processing Agreement"],
              ["/legal/acceptable-use",   "Acceptable Use Policy"],
            ].map(([href, label]) => (
              <Link
                key={href}
                to={href}
                className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-5 hover:border-[#0055FF]/40 hover:shadow-md hover:-translate-y-0.5 transition-all"
                data-testid={`trust-doc-${href.split("/").pop()}`}
              >
                <span className="flex items-center gap-3">
                  <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0055FF]/[0.08] text-[#0055FF] ring-1 ring-[#0055FF]/15 group-hover:bg-[#0055FF] group-hover:text-white transition-colors">
                    <FileText className="size-4" strokeWidth={1.9} />
                  </span>
                  <span className="text-[14.5px] font-medium text-slate-900">{label}</span>
                </span>
                <span className="text-[11px] font-mono text-slate-400 group-hover:text-[#0055FF]">→</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* RESPONSIBLE DISCLOSURE */}
      <section className="py-14 border-t border-slate-200 bg-gradient-to-b from-slate-900 to-[#0c1b3a] text-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Globe2 className="size-8 mx-auto text-[#7da9ff]" strokeWidth={1.6} />
          <h3 className="mt-4 text-3xl tracking-[-0.025em] font-semibold">Found a vulnerability?</h3>
          <p className="mt-3 text-white/70 max-w-xl mx-auto text-[15px] leading-relaxed">
            We welcome responsible disclosure. Send details to{" "}
            <a className="text-[#7da9ff] hover:text-white underline underline-offset-4" href="mailto:legal@connex.is">
              legal@connex.is
            </a>
            . We acknowledge within 24 hours and aim to triage within 72.
          </p>
        </div>
      </section>
    </div>
  );
}
